// src/data/warRoomDrafts.js - Record 657 (Bob 2026-10-12, "All": War Room items 2-6). Drafts and summaries the General
// Chairperson reads, reviews and sends herself - the platform sends nothing to the carrier and decides nothing. Every fact
// is read from the claim as recorded; the agreement's words are quoted exactly as the platform holds them (PD-16) and
// never supplied where it holds none. PURE.
import { clockRows, patterns, carrierRecord, idOf, labelOf } from './warRoomBoard';
import { claimActs } from './offProperty';

const s = (v) => (v == null ? '' : String(v)).trim();
export const DRAFT_MARK = 'DRAFT - review every line before it is sent. Nothing here has been sent to anyone.';

// ---- item 3: the carrier default notice, from a Clocks row flagged CARRIER DEFAULT ----------------------------------
export function defaultNoticeDraft(row, user, committee, todayIso) {
  const r = row || {}; const c = r.claim || {};
  const d = r.default || {};
  const to = s(c.presentedTo) || '[the carrier officer the claim was presented to]';
  const served = s(c.presentedOn) || '[the date the claim was served]';
  const lines = [
    DRAFT_MARK, '',
    s(todayIso), '',
    'To: ' + to, '',
    'Re: Claim ' + labelOf(c) + (s(c.claimantName) ? ' - ' + s(c.claimantName) : '') + ' - no answer within the time allowed', '',
    'The Organization presented the above claim on ' + served + '. The time for the Carrier\'s answer ran on ' + s(r.date)
      + '. No answer to the claim has been received.',
  ];
  if (d.onSilence) {
    lines.push('', 'The agreement provides: "' + d.onSilence + '"' + (d.cite ? ' (' + d.cite + ')' : '') + '.');
    lines.push('', 'The Organization therefore requests that the claim be allowed as presented.');
  } else {
    lines.push('', '[The platform holds no words of this agreement on the Carrier\'s failure to answer. Cite the provision here, '
      + 'word for word from the signed agreement, before this is sent.]');
  }
  lines.push('', s(user && (user.name || user.email)) || '[name]', 'General Chairperson' + (s(committee) ? ', ' + s(committee) : ''), '',
    'Before sending: confirm no answer was received, by any route, on any date.');
  return lines.join('\n');
}

// ---- item 4: the pattern claim draft, from a Patterns row -----------------------------------------------------------
export function patternClaimDraft(dimensionLabel, row) {
  const r = row || {};
  const cs = (r.claims || []).slice().sort((a, b) => s(a.incidentDate || a.dateOfIncident).localeCompare(s(b.incidentDate || b.dateOfIncident)));
  const lines = [DRAFT_MARK, '',
    'Facts for a continuing-violation or pattern claim - ' + s(dimensionLabel) + ': ' + s(r.label), '',
    'The same ' + s(dimensionLabel).replace(/^By /i, '').toLowerCase() + ' appears on ' + cs.length + ' claims of this committee:'];
  for (const c of cs) {
    lines.push('- ' + labelOf(c) + (s(c.incidentDate || c.dateOfIncident) ? ', ' + s(c.incidentDate || c.dateOfIncident) : '')
      + (s(c.claimantName) ? ', ' + s(c.claimantName) : '') + (s(c.status) ? ' (' + s(c.status) + ')' : ''));
  }
  lines.push('', 'These are the records only. Whether they make a pattern the agreement recognises, and whether to file, is the '
    + 'General Chairperson\'s decision; nothing above is a finding.');
  return lines.join('\n');
}

// ---- item 5: what the Board stage of a claim records - docket and award ---------------------------------------------
export function boardFacts(claim) {
  const acts = claimActs(claim || {});
  const sub = acts.filter(a => a.kind === 'board_submitted').slice(-1)[0];
  const notice = acts.filter(a => a.kind === 'board_notice').slice(-1)[0];
  const award = acts.filter(a => a.kind === 'award_issued').slice(-1)[0];
  const out = [];
  if (notice || sub) out.push(s((sub || notice).board) + (sub && s(sub.docketNumber) ? ', docket ' + s(sub.docketNumber) : ''));
  if (award) out.push('Award' + (s(award.on) ? ' ' + s(award.on) : '') + ': ' + s(award.awardOutcome) + (s(award.awardRef) ? ' (' + s(award.awardRef) + ')' : ''));
  return out;
}

// ---- item 2: the meeting prep packet - every claim on an occasion's agenda, its clocks, patterns and carrier record ---
export function prepPacket(session, claims, todayIso) {
  const ss = session || {};
  const all = claims || [];
  const items = (Array.isArray(ss.agendaItems) ? ss.agendaItems : []).filter(x => x && x.claimId);
  const byId = new Map(all.map(c => [idOf(c), c]));
  const pats = patterns(all);
  const lines = ['MEETING PREP PACKET - ' + s(ss.title) + (s(ss.scheduledFor) ? ' - ' + s(ss.scheduledFor) : ''), 'Prepared ' + s(todayIso)
    + ' from the claims as recorded. For the convener; the invitees see the agenda titles only.', ''];
  if (!items.length) lines.push('No claim is on this agenda.');
  for (const it of items) {
    const c = byId.get(String(it.claimId));
    lines.push('== ' + (it.claimLabel || it.title));
    if (!c) { lines.push('   (this claim is not among the claims you can read now)', ''); continue; }
    lines.push('   ' + [s(c.claimantName), s(c.railroad), s(c.status)].filter(Boolean).join(' - '));
    const clocks = clockRows([c], todayIso);
    if (!clocks.length) lines.push('   Clocks: none running.');
    for (const k of clocks) lines.push('   Clock: ' + k.date + ' - ' + k.title + ' - ' + (k.side === 'carrier' ? 'carrier' : 'Organization')
      + (k.days < 0 ? ' - ' + (-k.days) + ' days overdue' : ' - ' + k.days + ' days left') + (k.default ? ' - CARRIER DEFAULT? confirm no answer' : ''));
    const inPat = [];
    for (const d of pats) for (const r of d.rows) if (r.claims.some(x => idOf(x) === idOf(c))) inPat.push(d.label + ': ' + r.label + ' (' + r.n + ' claims)');
    lines.push('   Patterns: ' + (inPat.length ? inPat.join('; ') : 'none'));
    const cr = carrierRecord([c], todayIso).filter(x => x && x.officer);
    if (cr.length) lines.push('   Carrier record: ' + cr.map(x => x.officer + ' - ' + [x.denied ? 'denied' : '', x.allowed ? 'allowed' : '', x.partly ? 'partly allowed' : '', x.late ? 'answered late' : '', x.pastDue ? 'no answer, past due' : ''].filter(Boolean).join(', ')).join('; '));
    const bf = boardFacts(c);
    if (bf.length) lines.push('   Board: ' + bf.join('; '));
    lines.push('');
  }
  return lines.join('\n');
}
